import React, { useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import LetterViewer from "../components/LetterViewer";
import CounterShiftFilter, { isCounterUser, scopeLabel, useCounterShiftMeta } from "../components/CounterShiftFilter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Search, Eye, Printer, Pencil, Ban } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["PENDING_APPROVAL", "APPROVED", "PHOTO_CAPTURED", "ISSUED", "DARSHAN_COMPLETED", "REJECTED", "CANCELLED"];

export default function Transactions() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({ search: "", status: "", booking_type: "", date: "", shift_id: "", counter_id: "" });
  const csMeta = useCounterShiftMeta();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [reprintReason, setReprintReason] = useState("");
  const [editTxn, setEditTxn] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [darshanTypes, setDarshanTypes] = useState([]);

  useEffect(() => {
    if (user?.role === "SUPER_ADMIN")
      api.get("/meta/form-data").then((r) => setDarshanTypes(r.data.darshan_types)).catch(() => {});
  }, [user]);

  const load = (p = 1) => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && q.set(k, v));
    q.set("page", p);
    api.get(`/protocol?${q}`).then((r) => { setRows(r.data.rows); setTotal(r.data.total); setPage(p); }).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => { load(1); }, []); // eslint-disable-line

  const openEdit = (t) => {
    setEditTxn(t);
    setEditForm({
      devotee_name: t.devotee_name, reference_name: t.reference_name || "", mobile: t.mobile,
      num_devotees: t.num_devotees, darshan_type_id: t.darshan_type_id, paid_free: t.paid_free,
      paid_devotees: t.paid_devotees ?? (t.paid_free === "PAID" ? t.num_devotees : 0),
      ticket_no: t.ticket?.ticket_no || "", reason: "",
    });
  };

  const saveEdit = async () => {
    if (!editForm.reason?.trim()) { toast.error("Correction reason is required"); return; }
    try {
      await api.put(`/protocol/${editTxn.transaction_id}/correct`, { ...editForm, num_devotees: Number(editForm.num_devotees) });
      toast.success("Transaction corrected (audited)");
      setEditTxn(null);
      load(page);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const cancelTxn = async (t) => {
    const reason = window.prompt(`Cancellation reason for ${t.transaction_id}:`);
    if (!reason) return;
    try {
      await api.post(`/protocol/${t.transaction_id}/cancel`, { reason });
      toast.success("Transaction cancelled");
      load(page);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const reprint = async () => {
    if (!reprintReason.trim()) { toast.error("Reason for reprint is required"); return; }
    try {
      await api.post(`/tickets/${selected.transaction_id}/reprint`, { reason: reprintReason });
      toast.success("Reprint recorded");
      setReprintReason("");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="transactions-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-1">Transaction Search</h1>
      <p className="text-sm text-slate-500 mb-5">
        Search by Transaction ID, devotee name, mobile, reference or ticket number.
        {isCounterUser(user) && <span data-testid="txn-counter-scope"> Showing transactions for your counter only.</span>}
      </p>

      <Card className="shadow-sm mb-4">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div className="col-span-2"><Label className="text-xs">Search</Label>
            <Input data-testid="txn-search-input" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Transaction ID / name / mobile / ticket no" onKeyDown={(e) => e.key === "Enter" && load(1)} /></div>
          <div><Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="txn-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Booking Type</Label>
            <Select value={filters.booking_type} onValueChange={(v) => setFilters({ ...filters, booking_type: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="txn-type-filter"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All</SelectItem><SelectItem value="ADVANCED">Advanced</SelectItem><SelectItem value="CURRENT">Current</SelectItem></SelectContent>
            </Select></div>
          <CounterShiftFilter user={user} meta={csMeta} value={filters} onChange={setFilters} testid="txn" />
          <div className="flex gap-2 items-end">
            <div><Label className="text-xs">Date</Label><Input data-testid="txn-date-filter" type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} /></div>
            <Button data-testid="txn-search-btn" onClick={() => load(1)} className="bg-maroon-700 hover:bg-maroon-800 text-white"><Search className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="overflow-x-auto p-4">
          <p className="text-xs text-slate-500 mb-2" data-testid="txn-total">{total} record(s){scopeLabel(user, csMeta, filters)}</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
                <th className="py-2 pr-4">Transaction ID</th><th className="py-2 pr-4">Devotee</th><th className="py-2 pr-4">Mobile</th>
                <th className="py-2 pr-4">Darshan</th><th className="py-2 pr-4">Type</th><th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">Date</th><th className="py-2">View</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">No transactions found</td></tr>}
              {rows.map((t) => (
                <tr key={t.transaction_id} className="border-b border-[#F1EADB] hover:bg-gold-50/50" data-testid={`txn-row-${t.transaction_id}`}>
                  <td className="py-2 pr-4 font-mono text-xs">{t.transaction_id}</td>
                  <td className="py-2 pr-4">{t.devotee_name}</td>
                  <td className="py-2 pr-4">{t.mobile}</td>
                  <td className="py-2 pr-4">{t.darshan_type_name}</td>
                  <td className="py-2 pr-4 text-xs">{t.booking_type}</td>
                  <td className="py-2 pr-4"><StatusBadge status={t.status} /></td>
                  <td className="py-2 pr-4">₹{t.amount}</td>
                  <td className="py-2 pr-4 text-xs text-slate-500">{new Date(t.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="py-2 flex gap-1">
                    <Button size="sm" variant="ghost" data-testid={`txn-view-${t.transaction_id}`} onClick={() => setSelected(t)}><Eye className="w-4 h-4" /></Button>
                    {user.role === "SUPER_ADMIN" && (
                      <Button size="sm" variant="ghost" title="Correct transaction" data-testid={`txn-edit-${t.transaction_id}`} onClick={() => openEdit(t)}>
                        <Pencil className="w-4 h-4 text-saffron-600" />
                      </Button>
                    )}
                    {["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user.role) && !["CANCELLED", "REJECTED", "DARSHAN_COMPLETED"].includes(t.status) && (
                      <Button size="sm" variant="ghost" title="Cancel transaction" data-testid={`txn-cancel-${t.transaction_id}`} onClick={() => cancelTxn(t)}>
                        <Ban className="w-4 h-4 text-red-600" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex gap-2 mt-3 justify-end">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="txn-prev-btn">Prev</Button>
            <Button size="sm" variant="outline" disabled={page * 25 >= total} onClick={() => load(page + 1)} data-testid="txn-next-btn">Next</Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="txn-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">Transaction History</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><p className="text-xs text-gold-700 font-bold uppercase">Transaction ID</p><p className="font-mono">{selected.transaction_id}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Status</p><StatusBadge status={selected.status} /></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Devotee</p><p>{selected.devotee_name} ({selected.num_devotees})</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Created By</p><p>{selected.created_by_name}</p></div>
                {selected.ticket && <div><p className="text-xs text-gold-700 font-bold uppercase">Ticket No</p><p className="font-mono">{selected.ticket.ticket_no}</p></div>}
                {selected.letterhead_name && <div><p className="text-xs text-gold-700 font-bold uppercase">Letterhead</p><p>{selected.letterhead_name}</p></div>}
              </div>
              {selected.booking_type === "ADVANCED" && <LetterViewer txnId={selected.transaction_id} />}
              <div>
                <p className="text-xs text-gold-700 font-bold uppercase mb-2">Audit Trail</p>
                <ol className="relative border-l-2 border-gold-300 ml-2 space-y-3" data-testid="txn-history">
                  {(selected.history || []).map((h, i) => (
                    <li key={i} className="ml-4">
                      <div className="absolute -left-[7px] mt-1 w-3 h-3 rounded-full bg-maroon-700 border-2 border-gold-400" />
                      <p className="font-semibold text-maroon-900">{h.status.replace(/_/g, " ")}</p>
                      <p className="text-xs text-slate-500">{new Date(h.at).toLocaleString("en-IN")} · {h.by} {h.note ? `· ${h.note}` : ""}</p>
                    </li>
                  ))}
                </ol>
              </div>
              {selected.ticket && (
                <div className="border-t border-[#E5D9C5] pt-3 space-y-2">
                  <p className="text-xs text-gold-700 font-bold uppercase flex items-center gap-1"><Printer className="w-3 h-3" /> Reprint Control</p>
                  <div className="flex gap-2">
                    <Textarea rows={1} placeholder="Reason for reprint" value={reprintReason} onChange={(e) => setReprintReason(e.target.value)} data-testid="reprint-reason" />
                    <Button variant="outline" onClick={reprint} data-testid="reprint-btn">Request Reprint</Button>
                  </div>
                  {(selected.ticket.reprints || []).length > 0 && (
                    <p className="text-xs text-slate-500">{selected.ticket.reprints.length} reprint(s) logged.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTxn} onOpenChange={() => setEditTxn(null)}>
        <DialogContent className="max-w-xl" data-testid="txn-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">Transaction Correction (Super Admin)</DialogTitle></DialogHeader>
          {editTxn && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 font-mono">{editTxn.transaction_id} · <StatusBadge status={editTxn.status} /></p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Devotee Name</Label><Input value={editForm.devotee_name || ""} onChange={(e) => setEditForm({ ...editForm, devotee_name: e.target.value })} data-testid="edit-devotee-name" /></div>
                <div><Label className="text-xs">Reference Name</Label><Input value={editForm.reference_name || ""} onChange={(e) => setEditForm({ ...editForm, reference_name: e.target.value })} data-testid="edit-reference-name" /></div>
                <div><Label className="text-xs">Mobile</Label><Input value={editForm.mobile || ""} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} data-testid="edit-mobile" /></div>
                <div><Label className="text-xs">Number of Devotees</Label><Input type="number" min={1} max={20} value={editForm.num_devotees || 1} onChange={(e) => setEditForm({ ...editForm, num_devotees: e.target.value })} data-testid="edit-num-devotees" /></div>
                <div><Label className="text-xs">Darshan Type</Label>
                  <Select value={editForm.darshan_type_id || ""} onValueChange={(v) => setEditForm({ ...editForm, darshan_type_id: v })}>
                    <SelectTrigger data-testid="edit-darshan-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{darshanTypes.filter((d) => d.temple_id === editTxn.temple_id).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Paid Devotees</Label>
                  <Input type="number" min={0} max={editForm.num_devotees} value={editForm.paid_devotees ?? 0}
                    onChange={(e) => setEditForm({ ...editForm, paid_devotees: Math.max(0, Math.min(Number(e.target.value) || 0, Number(editForm.num_devotees) || 1)) })}
                    data-testid="edit-paid-devotees" />
                  <p className="text-[10px] text-slate-400 mt-0.5">Free devotees = total − paid. Amount recomputed automatically.</p>
                </div>
                {editTxn.ticket && (
                  <div><Label className="text-xs">Ticket No (correction)</Label>
                    <Input value={editForm.ticket_no || ""} onChange={(e) => setEditForm({ ...editForm, ticket_no: e.target.value })} data-testid="edit-ticket-no" />
                    <p className="text-[10px] text-slate-400 mt-0.5">Original number is preserved in ticket history.</p>
                  </div>
                )}
              </div>
              <div><Label className="text-xs">Correction Reason *</Label>
                <Textarea rows={2} value={editForm.reason || ""} onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} placeholder="Required — recorded in audit log with old/new values" data-testid="edit-reason" /></div>
              <p className="text-[11px] text-slate-500">Every correction records old value, new value, user, timestamp and IP. Original history is preserved.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditTxn(null)}>Close</Button>
                <Button onClick={saveEdit} className="bg-saffron-500 hover:bg-saffron-600 text-white" data-testid="edit-save-btn">Save Correction</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
